import { type RefObject, useEffect, useState } from "react"

import { findLiveController } from "@/lib/player/bridges"
import type { PlayableItem } from "@/lib/player/playable-item"

/**
 * Slide-local read of "is the audio actually playing?", for the open stage's
 * spinning record. Only audio kinds spin, and only while the stage is out.
 *
 * The bridge controller isn't ours to hold: `content-player`'s LiveIframe
 * attaches it asynchronously on iframe mount and registers it in the
 * liveControllers WeakMap (content-player is do-not-modify). So we poll
 * `findLiveController` off the slide root until the controller appears — a
 * handful of frames after the well mounts — then subscribe to its play state.
 * This reuses the exact seam the shell already uses to mute in place.
 *
 * Defaults to not-playing: Spotify's two-tap means the record stays still
 * until the listener presses play on the embed, which is the honest read.
 */
export function useVinylPlayback({
  rootRef,
  item,
  staged,
}: {
  rootRef: RefObject<HTMLElement | null>
  item: PlayableItem
  staged: boolean
}): { playing: boolean } {
  const [playing, setPlaying] = useState(false)
  const active = item.kind === "audio" && staged

  useEffect(() => {
    // When inactive the effect does nothing and its cleanup (below) has
    // already reset `playing` to false — no synchronous setState here.
    if (!active) return

    let unsubscribe: (() => void) | null = null
    let timer: number | null = null
    let attempts = 0

    const tryAttach = () => {
      const controller = findLiveController(rootRef.current)
      if (controller) {
        unsubscribe = controller.subscribePlayState(setPlaying)
        return
      }
      // ~5s of retries (50 × 100ms) covers a slow embed handshake; past that
      // the embed has no bridge and the record simply never spins.
      if (attempts++ < 50) {
        timer = window.setTimeout(tryAttach, 100)
      }
    }
    tryAttach()

    return () => {
      if (timer !== null) window.clearTimeout(timer)
      unsubscribe?.()
      setPlaying(false)
    }
  }, [active, rootRef])

  return { playing }
}
